import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetContractorsQuery,
  useCreateContractorMutation,
  useUpdateContractorMutation,
  useDeleteContractorMutation,
} from './contractorsApiSlice';
import { Loader } from 'semantic-ui-react';
import { NAV } from '../../navLabels';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { BandButton, ChipRow, Dash, ListNote, ListPageShell, ListTable, RowAction, Tag, cardAction } from '../../components/listPage';
import { shortDate } from '../chat_sessions/MobileChatSessionList';
import {
  ui,
  Pill,
  Modal,
  fieldStyle,
  labelStyle,
} from '../../components/adminUi';

const ContractorModal = ({ open, onClose, contractor = null }) => {
  const [createContractor] = useCreateContractorMutation();
  const [updateContractor] = useUpdateContractorMutation();
  const [name, setName] = useState(contractor?.name || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(contractor?.name || '');
  }, [contractor, open]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (contractor) {
        await updateContractor({ id: contractor.id, name }).unwrap();
      } else {
        await createContractor({ name }).unwrap();
      }
      onClose();
    } catch (err) {
      console.error('Failed to save contractor:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={contractor ? '파트너 수정' : '새 파트너 추가'}
      footer={
        <>
          <Pill variant="ghost" onClick={onClose} disabled={saving}>취소</Pill>
          <Pill variant="primary" onClick={handleSubmit} disabled={saving || !name.trim()}>
            {saving ? '저장 중…' : '저장'}
          </Pill>
        </>
      }
    >
      <label style={labelStyle}>파트너 이름</label>
      <input
        style={fieldStyle}
        value={name}
        autoFocus
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && name.trim() && handleSubmit()}
      />
    </Modal>
  );
};

const ContractorManagement = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const currentUser = useSelector(state => state.auth.user);
  const isSuperuser = currentUser?.is_superuser;
  const { data, isLoading } = useGetContractorsQuery();
  const [deleteContractor] = useDeleteContractorMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const contractors = Array.isArray(data) ? data : data?.results || [];

  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingContractor(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (confirmDelete) {
      try {
        await deleteContractor(confirmDelete.id).unwrap();
      } catch (err) {
        console.error('Failed to delete contractor:', err);
      }
      setConfirmDelete(null);
    }
  };

  if (isLoading) return <Loader active />;

  const columns = [
    { key: 'name', label: '이름', render: c => <span style={{ fontWeight: 600 }}>{c.name}</span> },
    { key: 'users', label: '사용자 수', render: c => c.user_count ?? <Dash /> },
    { key: 'admins', label: '관리자 수', render: c => c.admin_count ?? <Dash /> },
    // 0 is not "nothing to show": assignment is exclusive, so those trainees get an empty roleplay screen.
    { key: 'personas', label: '고객 페르소나', render: c => (c.persona_count > 0 ? `${c.persona_count}명` : <Tag tone="amber">없음</Tag>) },
    { key: 'created', label: '생성일', muted: true, render: c => shortDate(c.created_at) },
    isSuperuser && {
      key: 'actions', label: '', align: 'right',
      render: c => (
        <div style={{ display: 'inline-flex', gap: '4px' }}>
          <RowAction icon="edit outline" title="수정" onClick={() => handleEdit(c)} />
          <RowAction icon="trash alternate outline" title="삭제" onClick={() => setConfirmDelete(c)} />
        </div>
      ),
    },
  ].filter(Boolean);

  return (
    <ListPageShell
      isMobile={isMobile}
      icon="building"
      title={isSuperuser ? NAV.CONTRACTORS : NAV.CONTRACTOR_ACCESS}
      subtitle={isSuperuser ? '파트너 현황 및 관리' : '초대 링크 및 관리자 권한'}
      actions={isSuperuser && <BandButton primary compact={isMobile} icon="plus" onClick={handleCreate}>새 파트너</BandButton>}
    >
      {/* The click-through hint used to be shown only to superusers, which is backwards: a
          파트너 관리자 sees exactly one row and the only reason they are on this page is what
          lies behind it (초대 링크, 관리자 지정). They are the ones who need it. */}
      <ListNote>
        {isSuperuser
          ? '파트너별 소속 사용자·관리자와 배정된 고객 페르소나 수입니다. 행을 클릭하면 파트너 상세에서 관리자, 초대 링크, 이 파트너 훈련생에게 보일 고객 페르소나를 관리할 수 있습니다.'
          : `회사 이름을 클릭하면 직원 초대 링크를 발급하고 파트너 관리자를 지정할 수 있습니다. 직원별 학습 현황과 가입 승인은 ‘${NAV.MY_STAFF}’에서 확인하세요.`}
      </ListNote>
      <ChipRow groups={[]} count={contractors.length} />
      <ListTable
        isMobile={isMobile}
        rows={contractors}
        columns={columns}
        onRowClick={c => navigate(`/admin/contractors/${c.id}`)}
        empty={isSuperuser ? '등록된 파트너가 없습니다. 우측 상단 "새 파트너"로 추가하세요.' : '등록된 파트너가 없습니다.'}
        card={{
          title: c => c.name,
          tags: c => [!c.persona_count && <Tag key="p" tone="amber">페르소나 없음</Tag>],
          meta: c => [`사용자 ${c.user_count ?? 0}명`, `관리자 ${c.admin_count ?? 0}명`, `페르소나 ${c.persona_count ?? 0}명`],
          body: c => `생성 ${shortDate(c.created_at)}`,
          actions: c => isSuperuser && [
            <button key="e" type="button" style={cardAction(false, true)} onClick={() => handleEdit(c)}>수정</button>,
            <button key="d" type="button" style={cardAction(false, true)} onClick={() => setConfirmDelete(c)}>삭제</button>,
          ],
        }}
      />

      <ContractorModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingContractor(null); }}
        contractor={editingContractor}
      />

      <Modal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="파트너 삭제"
        width={400}
        footer={
          <>
            <Pill variant="ghost" onClick={() => setConfirmDelete(null)}>취소</Pill>
            <Pill variant="danger" onClick={handleDelete}>삭제</Pill>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: ui.text, lineHeight: 1.6 }}>
          <b>{confirmDelete?.name}</b> 파트너를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </p>
      </Modal>
    </ListPageShell>
  );
};

export default ContractorManagement;
